import { createSlice } from '@reduxjs/toolkit';
import * as MachineDashboardActionsTypes from '../../types/machineDashboardActions.types';
import * as MachineDashboardStateTypes from '../../types/machineDashboardState.types';
import {
  createInitialWidgetMachineListState,
  IWidgetMachineListsState,
  MACHINE_DASHBOARD_WIDGET_IDS,
} from '../../types/machineDashboardWidgetListState.types';

const initialManualMachineDashboardState: MachineDashboardStateTypes.IMachineDashboardData = {
  data: null,
  isLoading: null,
};

const initialWidgetListsState: IWidgetMachineListsState = MACHINE_DASHBOARD_WIDGET_IDS.reduce(
  (lists, widgetId) => ({
    ...lists,
    [widgetId]: createInitialWidgetMachineListState(),
  }),
  {} as IWidgetMachineListsState
);

export const initialState: MachineDashboardStateTypes.IMachineDashboardState = {
  manualMachineDashboard: initialManualMachineDashboardState,
  widgetLists: initialWidgetListsState,
  sitesList: {
    data: null,
    isLoading: false,
  },
  noSiteMachines: {
    data: null,
    isLoading: false,
  },
};

const machineDashboardSlice = createSlice({
  name: 'machineDashboard',
  initialState,
  reducers: {
    machineDashboardManualMachineDashboardRequest: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardRequestAction
    ) => {
      state.manualMachineDashboard.isLoading = true;
      return state;
    },
    machineDashboardManualMachineDashboardSuccess: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardSuccessAction
    ) => {
      state.manualMachineDashboard.data = action.payload.data;
      state.manualMachineDashboard.isLoading = false;
      return state;
    },
    machineDashboardManualMachineDashboardError: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardErrorAction
    ) => {
      state.manualMachineDashboard.isLoading = false;
      return state;
    },
    machineDashboardManualMachineDashboardResetState: state => {
      state.manualMachineDashboard = initialManualMachineDashboardState;
      return state;
    },
    machineDashboardManualMachineDashboardCancel: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardCancelAction
    ) => {
      state.manualMachineDashboard = initialManualMachineDashboardState;
      return state;
    },

    machineDashboardWidgetListRequest: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardWidgetListRequestAction
    ) => {
      const { widgetId } = action.payload;
      state.widgetLists[widgetId].isLoading = true;
      state.widgetLists[widgetId].isLoadingMore = false;
      state.widgetLists[widgetId].machines = [];
      state.widgetLists[widgetId].thumbnailUrlsByMachineId = {};
      state.widgetLists[widgetId].nextPaginationToken = '';
      state.widgetLists[widgetId].hasMore = false;
      return state;
    },
    machineDashboardWidgetListLoadMoreRequest: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardWidgetListLoadMoreRequestAction
    ) => {
      const { widgetId } = action.payload;
      state.widgetLists[widgetId].isLoadingMore = true;
      return state;
    },
    machineDashboardWidgetListSuccess: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardWidgetListSuccessAction
    ) => {
      const {
        widgetId,
        machines,
        thumbnailUrlsByMachineId,
        nextPaginationToken,
        hasMore,
        isLoadMore,
        period,
        siteIds,
        machineIds,
      } = action.payload;

      state.widgetLists[widgetId].isLoading = false;
      state.widgetLists[widgetId].isLoadingMore = false;
      state.widgetLists[widgetId].machines = isLoadMore
        ? [...state.widgetLists[widgetId].machines, ...machines]
        : machines;
      state.widgetLists[widgetId].thumbnailUrlsByMachineId = isLoadMore
        ? { ...state.widgetLists[widgetId].thumbnailUrlsByMachineId, ...thumbnailUrlsByMachineId }
        : thumbnailUrlsByMachineId;
      state.widgetLists[widgetId].nextPaginationToken = nextPaginationToken;
      state.widgetLists[widgetId].hasMore = hasMore;
      state.widgetLists[widgetId].requestContext = { period, siteIds, machineIds };
      return state;
    },
    machineDashboardWidgetListError: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardWidgetListErrorAction
    ) => {
      const { widgetId, isLoadMore } = action.payload;

      if (isLoadMore) {
        state.widgetLists[widgetId].isLoadingMore = false;
      } else {
        state.widgetLists[widgetId].isLoading = false;
      }

      return state;
    },
    machineDashboardWidgetListsResetState: state => {
      state.widgetLists = initialWidgetListsState;
      return state;
    },

    machineDashboardSitesListRequest: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardSitesListRequestAction
    ) => {
      state.sitesList.isLoading = true;
      return state;
    },
    machineDashboardSitesListSuccess: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardSitesListSuccessAction
    ) => {
      state.sitesList.data = action.payload.sites.data;
      state.sitesList.isLoading = false;
      return state;
    },
    machineDashboardSitesListError: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardSitesListErrorAction
    ) => {
      state.sitesList.isLoading = false;
      return state;
    },

    machineDashboardNoSiteMachinesRequest: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardNoSiteMachinesRequestAction
    ) => {
      state.noSiteMachines.isLoading = true;
      return state;
    },
    machineDashboardNoSiteMachinesSuccess: (
      state,
      action: MachineDashboardActionsTypes.MachineDashboardNoSiteMachinesSuccessAction
    ) => {
      state.noSiteMachines.data = action.payload.machines;
      state.noSiteMachines.isLoading = false;
      return state;
    },
    machineDashboardNoSiteMachinesError: (
      state,
      _action: MachineDashboardActionsTypes.MachineDashboardNoSiteMachinesErrorAction
    ) => {
      state.noSiteMachines.isLoading = false;
      return state;
    },
  },
});

export const MachineDashboardActions = machineDashboardSlice.actions;
export const machineDashboardReducer = machineDashboardSlice.reducer;
